import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';

const SavedMeasurementsContext = createContext();

export const useSavedMeasurements = () => {
  const context = useContext(SavedMeasurementsContext);
  if (!context) {
    throw new Error('useSavedMeasurements must be used within a SavedMeasurementsProvider');
  }
  return context;
};

export const SavedMeasurementsProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [savedMeasurements, setSavedMeasurements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load saved measurements for current user with caching
  const loadSavedMeasurements = async (forceRefresh = false) => {
    if (!currentUser) {
      setSavedMeasurements([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Direct Firebase query
      const measurementsRef = collection(db, 'savedMeasurements');
      const q = query(
        measurementsRef,
        where('userId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);

      const measurements = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data
        };
      });

      setSavedMeasurements(measurements);
    } catch (err) {
      console.error('Error loading saved measurements:', err);
      setError('Failed to load saved measurements');
    } finally {
      setLoading(false);
    }
  };

  // Save new measurement set
  const saveMeasurementSet = async (measurementData) => {
    if (!currentUser) {
      throw new Error('User must be logged in to save measurements');
    }

    setLoading(true);
    setError(null);

    try {
      const measurementsRef = collection(db, 'savedMeasurements');
      const newMeasurement = {
        userId: currentUser.uid,
        name: measurementData.name,
        productType: measurementData.productType,
        measurements: measurementData.measurements,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(measurementsRef, newMeasurement);
      
      // Add to local state
      const newMeasurementWithId = {
        id: docRef.id,
        ...newMeasurement,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      setSavedMeasurements(prev => [newMeasurementWithId, ...prev]);
      
      // Invalidate cache to ensure fresh data on next load
      
      return docRef.id;
    } catch (err) {
      console.error('Error saving measurement set:', err);
      setError('Failed to save measurements');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update existing measurement set
  const updateMeasurementSet = async (measurementId, updateData) => {
    if (!currentUser) {
      throw new Error('User must be logged in to update measurements');
    }

    setLoading(true);
    setError(null);

    try {
      const measurementRef = doc(db, 'savedMeasurements', measurementId);
      const updatePayload = {
        ...updateData,
        updatedAt: serverTimestamp()
      };

      await updateDoc(measurementRef, updatePayload);
      
      // Update local state
      setSavedMeasurements(prev => 
        prev.map(measurement => 
          measurement.id === measurementId 
            ? { ...measurement, ...updateData, updatedAt: new Date() }
            : measurement
        )
      );
      
      // Invalidate cache to ensure fresh data on next load
    } catch (err) {
      console.error('Error updating measurement set:', err);
      setError('Failed to update measurements');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Delete measurement set
  const deleteMeasurementSet = async (measurementId) => {
    if (!currentUser) {
      throw new Error('User must be logged in to delete measurements');
    }

    setLoading(true);
    setError(null);

    try {
      const measurementRef = doc(db, 'savedMeasurements', measurementId);
      await deleteDoc(measurementRef);
      
      // Remove from local state
      setSavedMeasurements(prev => 
        prev.filter(measurement => measurement.id !== measurementId)
      );
      
      // Invalidate cache to ensure fresh data on next load
    } catch (err) {
      console.error('Error deleting measurement set:', err);
      setError('Failed to delete measurements');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Get measurements by product type
  const getMeasurementsByProductType = (productType) => {
    return savedMeasurements.filter(measurement => 
      measurement.productType === productType
    );
  };

  // Clear measurements cache
  const clearMeasurementsCache = () => {
    if (currentUser?.uid) {
      console.log('🗑️ Cleared measurements cache');
    }
  };

  // Load measurements when user changes
  useEffect(() => {
    loadSavedMeasurements();
  }, [currentUser]);

  const value = {
    savedMeasurements,
    loading,
    error,
    currentUser,
    loadSavedMeasurements,
    saveMeasurementSet,
    updateMeasurementSet,
    deleteMeasurementSet,
    getMeasurementsByProductType,
    clearMeasurementsCache
  };

  return (
    <SavedMeasurementsContext.Provider value={value}>
      {children}
    </SavedMeasurementsContext.Provider>
  );
};

export default SavedMeasurementsContext;
